import { Icon } from '@iconify/react';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import config from '../config';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { formatDisplayDate } from '../utils/formatDate';

const UserManagementLayer = () => {
    const { auth } = useAuth();
    const [allUsers, setAllUsers] = useState([]);
    const [b2cUsers, setB2cUsers] = useState([]);
    const [b2bUsers, setB2bUsers] = useState([]);
    const [employeeUsers, setEmployeeUsers] = useState([]);
    const [counts, setCounts] = useState({ total: 0, clients: 0, customers: 0, employees: 0, active: 0, banned: 0 });
    const [loading, setLoading] = useState(true);

    // Active Tab State: 'all' | 'b2b' | 'b2c' | 'employees'
    const [activeTab, setActiveTab] = useState('all');

    // Filter States
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [roleFilter, setRoleFilter] = useState('all');

    // Modal States: Add User
    const [showAddModal, setShowAddModal] = useState(false);
    const [addForm, setAddForm] = useState({
        full_name: '',
        email: '',
        password: '',
        phone: '',
        role: 'venue_owner',
        status: 'active'
    });
    const [showAddPassword, setShowAddPassword] = useState(false);
    const [isSubmittingAdd, setIsSubmittingAdd] = useState(false);

    // Modal States: Change Password
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);

    // Modal States: Edit User
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({
        id: null,
        full_name: '',
        email: '',
        phone: '',
        role: 'user',
        status: 'active'
    });
    const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const response = await auth.fetch(`${config.API_BASE_URL}/api/users`);
            const data = await response.json();
            
            setAllUsers(data.all || []);
            setB2cUsers(data.b2c || []);
            setB2bUsers(data.b2b || []);
            setEmployeeUsers(data.employees || []);
            if (data.counts) {
                setCounts(data.counts);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Failed to load users list');
        } finally {
            setLoading(false);
        }
    }, [auth]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Generate random strong password
    const generateRandomPassword = () => {
        const chars = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%&*';
        let pwd = '';
        for (let i = 0; i < 10; i++) {
            pwd += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return pwd;
    };

    // Handle Create User Submit
    const handleAddUserSubmit = async (e) => {
        e.preventDefault();
        if (!addForm.email || !addForm.password) {
            toast.error('Email and password are required');
            return;
        }

        if (addForm.password.length < 6) {
            toast.error('Password must be at least 6 characters long');
            return;
        }

        setIsSubmittingAdd(true);
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/users`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(addForm)
            });

            const data = await res.json();

            if (res.ok) {
                toast.success(`Account for ${addForm.full_name || addForm.email} created successfully`);
                setShowAddModal(false);
                setAddForm({
                    full_name: '',
                    email: '',
                    password: '',
                    phone: '',
                    role: 'venue_owner',
                    status: 'active'
                });
                fetchUsers();
            } else {
                toast.error(data.error || 'Failed to create account');
            }
        } catch (err) {
            console.error('Create user error:', err);
            toast.error('Error creating user account');
        } finally {
            setIsSubmittingAdd(false);
        }
    };

    // Handle Change Password Submit
    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (!newPassword || newPassword.length < 6) {
            toast.error('Password must be at least 6 characters long');
            return;
        }

        setIsSubmittingPassword(true);
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/users/${selectedUserForPassword.id}/admin-password`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password: newPassword })
            });

            const data = await res.json();

            if (res.ok) {
                toast.success(`Password for ${selectedUserForPassword.full_name || selectedUserForPassword.email} changed successfully`);
                setShowPasswordModal(false);
                setNewPassword('');
                setSelectedUserForPassword(null);
            } else {
                toast.error(data.error || 'Failed to update password');
            }
        } catch (err) {
            console.error('Password update error:', err);
            toast.error('Error updating password');
        } finally {
            setIsSubmittingPassword(false);
        }
    };

    // Handle Edit User Submit
    const handleEditUserSubmit = async (e) => {
        e.preventDefault();
        setIsSubmittingEdit(true);
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/users/${editForm.id}/admin-update`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    full_name: editForm.full_name,
                    email: editForm.email,
                    phone: editForm.phone,
                    role: editForm.role,
                    status: editForm.status
                })
            });

            const data = await res.json();

            if (res.ok) {
                toast.success('User account updated successfully');
                setShowEditModal(false);
                fetchUsers();
            } else {
                toast.error(data.error || 'Failed to update user');
            }
        } catch (err) {
            console.error('Update user error:', err);
            toast.error('Error updating user');
        } finally {
            setIsSubmittingEdit(false);
        }
    };

    const handleBan = async (user) => {
        const isBanned = user.status === 'banned';
        const action = isBanned ? 'unban' : 'ban';
        const confirmMsg = isBanned 
            ? `Are you sure you want to unban ${user.full_name || user.email}?` 
            : `Are you sure you want to ban ${user.full_name || user.email}? They will be logged out immediately.`;

        if (window.confirm(confirmMsg)) {
            try {
                const response = await auth.fetch(`${config.API_BASE_URL}/api/users/${user.id}/${action}`, {
                    method: 'PUT'
                });
                if (response.ok) {
                    toast.success(`User ${isBanned ? 'unbanned' : 'banned'} successfully`);
                    fetchUsers();
                } else {
                    const data = await response.json();
                    toast.error(data.error || `Failed to ${action} user`);
                }
            } catch (error) {
                console.error(`Error ${action}ning user:`, error);
                toast.error(`An error occurred while ${action}ning the user`);
            }
        }
    };

    const handleDelete = async (user) => {
        if (window.confirm(`Are you sure you want to delete ${user.full_name || user.email}? This action cannot be undone.`)) {
            try {
                const response = await auth.fetch(`${config.API_BASE_URL}/api/users/${user.id}`, {
                    method: 'DELETE'
                });
                if (response.ok) {
                    toast.success('User deleted successfully');
                    setAllUsers(prev => prev.filter(u => u.id !== user.id));
                    setB2cUsers(prev => prev.filter(u => u.id !== user.id));
                    setB2bUsers(prev => prev.filter(u => u.id !== user.id));
                    setEmployeeUsers(prev => prev.filter(u => u.id !== user.id));
                } else {
                    const data = await response.json();
                    toast.error(data.error || 'Failed to delete user');
                }
            } catch (error) {
                console.error('Error deleting user:', error);
                toast.error('An error occurred while deleting the user');
            }
        }
    };

    const openPasswordModal = (user) => {
        setSelectedUserForPassword(user);
        setNewPassword('');
        setShowPasswordModal(true);
    };

    const openEditModal = (user) => {
        setEditForm({
            id: user.id,
            full_name: user.full_name || '',
            email: user.email || '',
            phone: user.phone || '',
            role: user.role || 'user',
            status: user.status || 'active'
        });
        setShowEditModal(true);
    };

    // Filter Users based on Active Tab, Search, Status, and Role
    const filteredUsers = useMemo(() => {
        let list = [];
        if (activeTab === 'all') {
            list = allUsers;
        } else if (activeTab === 'b2b') {
            list = b2bUsers;
        } else if (activeTab === 'b2c') {
            list = b2cUsers;
        } else if (activeTab === 'employees') {
            list = employeeUsers;
        }

        return list.filter(user => {
            // Search filter
            if (searchTerm.trim() !== '') {
                const term = searchTerm.toLowerCase();
                const matchName = user.full_name && user.full_name.toLowerCase().includes(term);
                const matchEmail = user.email && user.email.toLowerCase().includes(term);
                const matchPhone = user.phone && user.phone.toLowerCase().includes(term);
                const matchId = String(user.id).includes(term);
                if (!matchName && !matchEmail && !matchPhone && !matchId) {
                    return false;
                }
            }

            // Status filter
            if (statusFilter !== 'all' && user.status !== statusFilter) {
                return false;
            }

            // Role filter
            if (roleFilter !== 'all' && user.role !== roleFilter) {
                return false;
            }

            return true;
        });
    }, [activeTab, allUsers, b2bUsers, b2cUsers, employeeUsers, searchTerm, statusFilter, roleFilter]);

    const getRoleBadge = (role) => {
        switch (role) {
            case 'venue_owner':
                return (
                    <span className="hs-badge hs-badge-info">
                        <Icon icon="solar:shop-2-bold" /> Venue Owner
                    </span>
                );
            case 'user':
                return (
                    <span className="hs-badge hs-badge-neutral">
                        <Icon icon="solar:user-bold" /> Mobile App User
                    </span>
                );
            case 'admin':
                return (
                    <span className="hs-badge hs-badge-danger">
                        <Icon icon="solar:shield-check-bold" /> Administrator
                    </span>
                );
            case 'employee':
                return (
                    <span className="hs-badge hs-badge-warning">
                        <Icon icon="solar:user-id-bold" /> Staff
                    </span>
                );
            default:
                return (
                    <span className="hs-badge hs-badge-neutral">
                        {role || 'User'}
                    </span>
                );
        }
    };

    const getInitials = (name, email) => {
        if (name && name.trim()) {
            return name.trim().split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
        }
        if (email) {
            return email.substring(0, 2).toUpperCase();
        }
        return 'U';
    };

    const getUserAvatarUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http://') || path.startsWith('https://')) return path;
        const cleanPath = path.startsWith('/') ? path.substring(1) : path;
        const baseUrl = (config.API_BASE_URL || '').replace(/\/+$/, '');
        return `${baseUrl}/${cleanPath}`;
    };

    return (
        <div className="d-flex flex-column gap-20">
            {/* Top Stat Cards */}
            <div className="row g-3">
                <div className="col-12 col-sm-6 col-lg-3">
                    <div className="hs-stat-card h-100" style={{ '--stat-accent': 'var(--hs-primary)' }}>
                        <div className="hs-stat-icon-wrapper bg-primary-50 text-primary-600">
                            <Icon icon="solar:users-group-two-rounded-bold" />
                        </div>
                        <div>
                            <span className="text-secondary-light text-xs fw-semibold text-uppercase d-block mb-1">Total Users</span>
                            <h4 className="fw-bold mb-0 text-dark">{counts.total || allUsers.length}</h4>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                    <div className="hs-stat-card h-100" style={{ '--stat-accent': 'var(--hs-info)' }}>
                        <div className="hs-stat-icon-wrapper bg-info-50 text-info-600">
                            <Icon icon="solar:shop-2-bold" />
                        </div>
                        <div>
                            <span className="text-secondary-light text-xs fw-semibold text-uppercase d-block mb-1">Venue Owners</span>
                            <h4 className="fw-bold mb-0 text-dark">{counts.clients || b2bUsers.length}</h4>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                    <div className="hs-stat-card h-100" style={{ '--stat-accent': 'var(--hs-success)' }}>
                        <div className="hs-stat-icon-wrapper bg-success-50 text-success-600">
                            <Icon icon="solar:smartphone-bold" />
                        </div>
                        <div>
                            <span className="text-secondary-light text-xs fw-semibold text-uppercase d-block mb-1">Mobile App Users</span>
                            <h4 className="fw-bold mb-0 text-dark">{counts.customers || b2cUsers.length}</h4>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                    <div className="hs-stat-card h-100" style={{ '--stat-accent': 'var(--hs-warning)' }}>
                        <div className="hs-stat-icon-wrapper bg-warning-50 text-warning-600">
                            <Icon icon="solar:shield-check-bold" />
                        </div>
                        <div>
                            <span className="text-secondary-light text-xs fw-semibold text-uppercase d-block mb-1">Active Accounts</span>
                            <h4 className="fw-bold mb-0 text-dark">{counts.active || allUsers.filter(u => u.status === 'active').length}</h4>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main User Card */}
            <div className="hs-card">
                {/* Header, Tabs & Actions */}
                <div className="hs-card-header flex-column flex-lg-row align-items-stretch align-items-lg-center gap-3">
                    {/* Tabs */}
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        <div className="hs-nav-pills" role="tablist">
                            <button 
                                className={`hs-nav-link ${activeTab === 'all' ? 'active' : ''}`}
                                onClick={() => setActiveTab('all')}
                                type="button"
                            >
                                All Users ({allUsers.length})
                            </button>
                            <button 
                                className={`hs-nav-link ${activeTab === 'b2b' ? 'active' : ''}`}
                                onClick={() => setActiveTab('b2b')}
                                type="button"
                            >
                                Venue Owners ({b2bUsers.length})
                            </button>
                            <button 
                                className={`hs-nav-link ${activeTab === 'b2c' ? 'active' : ''}`}
                                onClick={() => setActiveTab('b2c')}
                                type="button"
                            >
                                Mobile App Users ({b2cUsers.length})
                            </button>
                            {employeeUsers.length > 0 && (
                                <button 
                                    className={`hs-nav-link ${activeTab === 'employees' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('employees')}
                                    type="button"
                                >
                                    Staff ({employeeUsers.length})
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Search, Filter & Add User Button */}
                    <div className="d-flex align-items-center flex-wrap gap-2">
                        {/* Status Filter */}
                        <select 
                            className="hs-select w-auto"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">All Statuses</option>
                            <option value="active">Active</option>
                            <option value="suspended">Inactive</option>
                            <option value="banned">Banned</option>
                        </select>

                        {/* Role Filter (Only show on 'all' tab) */}
                        {activeTab === 'all' && (
                            <select 
                                className="hs-select w-auto"
                                value={roleFilter}
                                onChange={(e) => setRoleFilter(e.target.value)}
                            >
                                <option value="all">All Roles</option>
                                <option value="venue_owner">Venue Owners</option>
                                <option value="user">Mobile App Users</option>
                                <option value="employee">Staff</option>
                                <option value="admin">Admins</option>
                            </select>
                        )}

                        {/* Search Box */}
                        <div className="hs-search-wrapper" style={{ maxWidth: '220px' }}>
                            <Icon icon="solar:magnifer-linear" className="hs-search-icon" />
                            <input 
                                type="text" 
                                className="hs-input" 
                                placeholder="Search user..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {/* Add User / Venue Owner Button */}
                        <button 
                            className="hs-btn-primary hs-btn-sm"
                            onClick={() => setShowAddModal(true)}
                        >
                            <Icon icon="solar:user-plus-bold" />
                            <span>Add Account</span>
                        </button>

                        {/* Refresh Button */}
                        <button 
                            className="hs-icon-btn hs-btn-sm"
                            onClick={fetchUsers}
                            title="Refresh Users"
                        >
                            <Icon icon="solar:refresh-bold" />
                        </button>
                    </div>
                </div>

                {/* Table Body */}
                <div className="card-body p-24">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading users...</span>
                            </div>
                            <p className="text-secondary mt-2">Loading user accounts...</p>
                        </div>
                    ) : filteredUsers.length === 0 ? (
                        <div className="text-center py-5 text-secondary">
                            <Icon icon="solar:user-cross-bold-duotone" className="text-5xl text-secondary-light mb-3" />
                            <h6 className="fw-semibold text-dark">No users found</h6>
                            <p className="text-sm text-secondary-light">
                                {searchTerm || statusFilter !== 'all' || roleFilter !== 'all' 
                                    ? 'Try changing your search term or filters.' 
                                    : 'There are no registered users in this category.'}
                            </p>
                        </div>
                    ) : (
                        <div className="hs-table-wrapper">
                            <table className="hs-table">
                                <thead>
                                    <tr>
                                        <th>User Profile</th>
                                        <th>Account Role</th>
                                        <th>Contact Information</th>
                                        <th>Venues &amp; Plan</th>
                                        <th className="text-center">Status</th>
                                        <th className="text-center">Registered Date</th>
                                        <th className="text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.map(user => (
                                        <tr key={user.id}>
                                            {/* User Profile */}
                                            <td>
                                                <div className="d-flex align-items-center gap-3">
                                                    {user.profile_image ? (
                                                        <img 
                                                            src={getUserAvatarUrl(user.profile_image)} 
                                                            alt={user.full_name || 'User'} 
                                                            className="w-40-px h-40-px rounded-circle object-fit-cover border"
                                                            onError={(e) => {
                                                                e.currentTarget.style.display = 'none';
                                                                const fallback = e.currentTarget.parentElement?.querySelector('.user-avatar-fallback');
                                                                if (fallback) fallback.classList.remove('d-none');
                                                            }}
                                                        />
                                                    ) : null}
                                                    <div className={`w-40-px h-40-px rounded-circle bg-primary-50 text-primary-600 fw-bold d-flex align-items-center justify-content-center border border-primary-100 flex-shrink-0 ${user.profile_image ? 'd-none user-avatar-fallback' : ''}`}>
                                                        {getInitials(user.full_name, user.email)}
                                                    </div>
                                                    <div>
                                                        <h6 className="text-sm mb-0 fw-semibold text-dark">
                                                            {user.full_name || 'Anonymous User'}
                                                        </h6>
                                                        <span className="text-xs text-secondary-light fw-medium">
                                                            ID: #{user.id}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Role */}
                                            <td>
                                                {getRoleBadge(user.role)}
                                            </td>

                                            {/* Contact */}
                                            <td>
                                                <div className="d-flex flex-column gap-1">
                                                    <div className="d-flex align-items-center gap-1 text-sm text-dark">
                                                        <Icon icon="solar:letter-bold" className="text-secondary-light" />
                                                        <span>{user.email}</span>
                                                        {user.is_email_verified ? (
                                                            <span title="Email Verified" className="text-success-main ms-1">
                                                                <Icon icon="solar:check-circle-bold" />
                                                            </span>
                                                        ) : (
                                                            <span title="Email Unverified" className="text-secondary-light ms-1">
                                                                <Icon icon="solar:close-circle-bold" />
                                                            </span>
                                                        )}
                                                    </div>
                                                    {user.phone ? (
                                                        <div className="d-flex align-items-center gap-1 text-xs text-secondary-light">
                                                            <Icon icon="solar:phone-bold" />
                                                            <span>{user.phone}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-secondary-light fst-italic">No phone</span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Venues / Plan */}
                                            <td>
                                                {user.role === 'venue_owner' ? (
                                                    <div className="d-flex flex-column gap-1">
                                                        <span className="hs-badge hs-badge-info align-self-start">
                                                            <Icon icon="solar:buildings-bold" className="me-1" />
                                                            {user.venue_count || 0} Venues
                                                        </span>
                                                        {user.plan_name && (
                                                            <span className="text-xs text-secondary-light">
                                                                Plan: <strong className="text-dark">{user.plan_name}</strong>
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-sm text-secondary-light">—</span>
                                                )}
                                            </td>

                                            {/* Status */}
                                            <td className="text-center">
                                                <span className={`hs-badge ${
                                                    user.status === 'active' 
                                                        ? 'hs-badge-active' 
                                                        : user.status === 'banned' 
                                                        ? 'hs-badge-rejected' 
                                                        : 'hs-badge-pending'
                                                }`}>
                                                    <span className="hs-badge-dot"></span>
                                                    {user.status ? user.status.toUpperCase() : 'ACTIVE'}
                                                </span>
                                            </td>

                                            {/* Registered Date */}
                                            <td className="text-center text-sm text-secondary-light">
                                                {user.created_at ? formatDisplayDate(user.created_at) : '—'}
                                            </td>

                                            {/* Action Dropdown */}
                                            <td className="text-center">
                                                <div className="dropdown">
                                                    <button 
                                                        className="hs-btn-secondary hs-btn-sm d-inline-flex align-items-center gap-1" 
                                                        type="button" 
                                                        data-bs-toggle="dropdown"
                                                        aria-expanded="false"
                                                    >
                                                        Actions <Icon icon="solar:alt-arrow-down-linear" />
                                                    </button>
                                                    <ul className="dropdown-menu dropdown-menu-end shadow-sm border radius-8 p-1">
                                                        <li>
                                                            <button 
                                                                className="dropdown-item d-flex align-items-center gap-2 py-8 radius-6 text-dark"
                                                                onClick={() => openPasswordModal(user)}
                                                            >
                                                                <Icon icon="solar:key-bold" className="text-primary-600" />
                                                                Change Password
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button 
                                                                className="dropdown-item d-flex align-items-center gap-2 py-8 radius-6 text-dark"
                                                                onClick={() => openEditModal(user)}
                                                            >
                                                                <Icon icon="solar:pen-bold" className="text-info-main" />
                                                                Edit Account
                                                            </button>
                                                        </li>
                                                        <li><hr className="dropdown-divider my-1" /></li>
                                                        <li>
                                                            <button 
                                                                className={`dropdown-item d-flex align-items-center gap-2 py-8 radius-6 ${user.status === 'banned' ? 'text-success-main' : 'text-warning-main'}`}
                                                                onClick={() => handleBan(user)}
                                                            >
                                                                <Icon icon={user.status === 'banned' ? 'solar:lock-unlocked-bold' : 'solar:lock-bold'} />
                                                                {user.status === 'banned' ? 'Unban Account' : 'Ban Account'}
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button 
                                                                className="dropdown-item d-flex align-items-center gap-2 py-8 radius-6 text-danger-main" 
                                                                onClick={() => handleDelete(user)}
                                                            >
                                                                <Icon icon="solar:trash-bin-trash-bold" />
                                                                Delete Account
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal 1: Add User / Venue Owner */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
                <Modal.Header closeButton className="border-bottom bg-white py-16 px-24">
                    <div className="d-flex align-items-center gap-2">
                        <div className="w-36-px h-36-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center">
                            <Icon icon="solar:user-plus-bold" className="text-xl" />
                        </div>
                        <div>
                            <Modal.Title className="text-lg fw-bold text-dark mb-0">Create New Account</Modal.Title>
                            <span className="text-xs text-secondary-light">Add a Venue Owner, Mobile App User, or Employee</span>
                        </div>
                    </div>
                </Modal.Header>
                <Form onSubmit={handleAddUserSubmit}>
                    <Modal.Body className="p-24">
                        <div className="d-flex flex-column gap-3">
                            <div>
                                <Form.Label className="fw-semibold text-dark text-sm mb-1">Account Role / Type *</Form.Label>
                                <Form.Select 
                                    value={addForm.role}
                                    onChange={(e) => setAddForm({ ...addForm, role: e.target.value })}
                                    className="bg-base radius-8"
                                >
                                    <option value="venue_owner">Venue Owner</option>
                                    <option value="user">Mobile App User</option>
                                    <option value="employee">Staff</option>
                                    <option value="admin">Administrator</option>
                                </Form.Select>
                            </div>

                            <div>
                                <Form.Label className="fw-semibold text-dark text-sm mb-1">Full Name</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    placeholder="e.g. John Doe / Marina Group"
                                    value={addForm.full_name}
                                    onChange={(e) => setAddForm({ ...addForm, full_name: e.target.value })}
                                    className="bg-base radius-8"
                                />
                            </div>

                            <div>
                                <Form.Label className="fw-semibold text-dark text-sm mb-1">Email Address *</Form.Label>
                                <Form.Control 
                                    type="email" 
                                    placeholder="user@example.com"
                                    required
                                    value={addForm.email}
                                    onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                                    className="bg-base radius-8"
                                />
                            </div>

                            <div>
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <Form.Label className="fw-semibold text-dark text-sm mb-0">Password *</Form.Label>
                                    <button 
                                        type="button" 
                                        className="btn btn-link btn-sm p-0 text-primary-600 text-xs text-decoration-none fw-semibold"
                                        onClick={() => setAddForm({ ...addForm, password: generateRandomPassword() })}
                                    >
                                        <Icon icon="solar:refresh-bold" className="me-1" /> Generate Random
                                    </button>
                                </div>
                                <div className="position-relative">
                                    <Form.Control 
                                        type={showAddPassword ? "text" : "password"}
                                        placeholder="Min. 6 characters"
                                        required
                                        value={addForm.password}
                                        onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                                        className="bg-base radius-8 pe-40"
                                    />
                                    <button 
                                        type="button" 
                                        className="btn btn-sm btn-link position-absolute end-0 top-50 translate-middle-y text-secondary me-2 p-0 text-decoration-none"
                                        onClick={() => setShowAddPassword(!showAddPassword)}
                                    >
                                        <Icon icon={showAddPassword ? "solar:eye-closed-bold" : "solar:eye-bold"} />
                                    </button>
                                </div>
                            </div>

                            <div>
                                <Form.Label className="fw-semibold text-dark text-sm mb-1">Phone Number</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    placeholder="+971 50 123 4567"
                                    value={addForm.phone}
                                    onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })}
                                    className="bg-base radius-8"
                                />
                            </div>

                            <div>
                                <Form.Label className="fw-semibold text-dark text-sm mb-1">Initial Status</Form.Label>
                                <Form.Select 
                                    value={addForm.status}
                                    onChange={(e) => setAddForm({ ...addForm, status: e.target.value })}
                                    className="bg-base radius-8"
                                >
                                    <option value="active">Active (Instant Access)</option>
                                    <option value="suspended">Inactive</option>
                                </Form.Select>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-top bg-white py-12 px-24">
                        <button type="button" className="hs-btn-secondary" onClick={() => setShowAddModal(false)}>
                            Cancel
                        </button>
                        <Button variant="primary" type="submit" className="radius-8 fw-semibold" disabled={isSubmittingAdd}>
                            {isSubmittingAdd ? 'Creating...' : 'Create Account'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal 2: Change Password */}
            <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered>
                <Modal.Header closeButton className="border-bottom bg-white py-16 px-24">
                    <div className="d-flex align-items-center gap-2">
                        <div className="w-36-px h-36-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center">
                            <Icon icon="solar:key-bold" className="text-xl" />
                        </div>
                        <div>
                            <Modal.Title className="text-lg fw-bold text-dark mb-0">Reset Password</Modal.Title>
                            <span className="text-xs text-secondary-light">
                                For: {selectedUserForPassword?.full_name || selectedUserForPassword?.email}
                            </span>
                        </div>
                    </div>
                </Modal.Header>
                <Form onSubmit={handlePasswordSubmit}>
                    <Modal.Body className="p-24">
                        <div className="d-flex flex-column gap-3">
                            <div className="p-12 bg-light rounded-3 text-xs text-dark">
                                <span className="fw-bold d-block mb-1">User Account:</span>
                                <div>Email: <strong>{selectedUserForPassword?.email}</strong></div>
                                <div>Role: <strong>{selectedUserForPassword?.role}</strong></div>
                            </div>

                            <div>
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <Form.Label className="fw-semibold text-dark text-sm mb-0">New Password *</Form.Label>
                                    <button 
                                        type="button" 
                                        className="btn btn-link btn-sm p-0 text-primary-600 text-xs text-decoration-none fw-semibold"
                                        onClick={() => setNewPassword(generateRandomPassword())}
                                    >
                                        <Icon icon="solar:refresh-bold" className="me-1" /> Generate Random
                                    </button>
                                </div>
                                <div className="position-relative">
                                    <Form.Control 
                                        type={showNewPassword ? "text" : "password"}
                                        placeholder="Enter new password (min. 6 chars)"
                                        required
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        className="bg-base radius-8 pe-40"
                                    />
                                    <button 
                                        type="button" 
                                        className="btn btn-sm btn-link position-absolute end-0 top-50 translate-middle-y text-secondary me-2 p-0 text-decoration-none"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                    >
                                        <Icon icon={showNewPassword ? "solar:eye-closed-bold" : "solar:eye-bold"} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-top bg-white py-12 px-24">
                        <button type="button" className="hs-btn-secondary" onClick={() => setShowPasswordModal(false)}>
                            Cancel
                        </button>
                        <Button variant="primary" type="submit" className="radius-8 fw-semibold" disabled={isSubmittingPassword}>
                            {isSubmittingPassword ? 'Updating...' : 'Update Password'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal 3: Edit User Details */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
                <Modal.Header closeButton className="border-bottom bg-white py-16 px-24">
                    <div className="d-flex align-items-center gap-2">
                        <div className="w-36-px h-36-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center">
                            <Icon icon="solar:pen-bold" className="text-xl" />
                        </div>
                        <div>
                            <Modal.Title className="text-lg fw-bold text-dark mb-0">Edit User Account</Modal.Title>
                            <span className="text-xs text-secondary-light">Update account profile, role, and status</span>
                        </div>
                    </div>
                </Modal.Header>
                <Form onSubmit={handleEditUserSubmit}>
                    <Modal.Body className="p-24">
                        <div className="d-flex flex-column gap-3">
                            <div>
                                <Form.Label className="fw-semibold text-dark text-sm mb-1">Full Name</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    value={editForm.full_name}
                                    onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                                    className="bg-base radius-8"
                                />
                            </div>

                            <div>
                                <Form.Label className="fw-semibold text-dark text-sm mb-1">Email Address *</Form.Label>
                                <Form.Control 
                                    type="email" 
                                    required
                                    value={editForm.email}
                                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                    className="bg-base radius-8"
                                />
                            </div>

                            <div>
                                <Form.Label className="fw-semibold text-dark text-sm mb-1">Phone Number</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    value={editForm.phone}
                                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                                    className="bg-base radius-8"
                                />
                            </div>

                            <div>
                                <Form.Label className="fw-semibold text-dark text-sm mb-1">Role</Form.Label>
                                <Form.Select 
                                    value={editForm.role}
                                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                                    className="bg-base radius-8"
                                >
                                    <option value="venue_owner">Venue Owner</option>
                                    <option value="user">Mobile App User</option>
                                    <option value="employee">Staff</option>
                                    <option value="admin">Administrator</option>
                                </Form.Select>
                            </div>

                            <div>
                                <Form.Label className="fw-semibold text-dark text-sm mb-1">Account Status</Form.Label>
                                <Form.Select 
                                    value={editForm.status}
                                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                                    className="bg-base radius-8"
                                >
                                    <option value="active">Active</option>
                                    <option value="suspended">Inactive</option>
                                    <option value="banned">Banned</option>
                                </Form.Select>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-top bg-white py-12 px-24">
                        <button type="button" className="hs-btn-secondary" onClick={() => setShowEditModal(false)}>
                            Cancel
                        </button>
                        <Button variant="primary" type="submit" className="radius-8 fw-semibold" disabled={isSubmittingEdit}>
                            {isSubmittingEdit ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default UserManagementLayer;
